import { useState, useEffect } from 'react';
import { getVehicles, createVehicle, deleteVehicle, updateVehicle, type Vehicle } from '../../api/vehicles.api';

export default function Vehiculos() {
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState('TODOS');
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    placa: '',
    tipo: 'AUTO',
    marca: '',
    modelo: '',
    color: '',
    propietario: '',
    telefono: ''
  });

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      setIsLoading(true);
      const data = await getVehicles();
      setVehicles(data);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (vehicle: Vehicle) => {
      setFormData({
          placa: vehicle.placa,
          tipo: vehicle.tipo,
          marca: vehicle.marca || '',
          modelo: vehicle.modelo || '',
          color: vehicle.color || '',
          propietario: vehicle.propietario || '',
          telefono: vehicle.telefono || ''
      });
      setEditingId(vehicle.id);
      setViewMode(false);
      setShowModal(true);
  };

  const handleView = (vehicle: Vehicle) => {
      setFormData({
          placa: vehicle.placa,
          tipo: vehicle.tipo,
          marca: vehicle.marca || '',
          modelo: vehicle.modelo || '',
          color: vehicle.color || '',
          propietario: vehicle.propietario || '',
          telefono: vehicle.telefono || ''
      });
      setEditingId(vehicle.id);
      setViewMode(true);
      setShowModal(true);
  };

  const handleCloseModal = () => {
      setShowModal(false);
      setEditingId(null);
      setViewMode(false);
      setFormData({
        placa: '',
        tipo: 'AUTO',
        marca: '',
        modelo: '',
        color: '',
        propietario: '',
        telefono: ''
      });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (viewMode) return;

    setIsSubmitting(true);
    try {
      if (editingId) {
          await updateVehicle(editingId, formData);
      } else {
          await createVehicle(formData);
      }
      await loadVehicles();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      alert('Error al guardar vehículo. Verifique los datos.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar vehículo?')) return;
    try {
        await deleteVehicle(id);
        await loadVehicles();
    } catch (error) {
        console.error('Error deleting vehicle:', error);
        alert('No se pudo eliminar el vehículo.');
    }
  };

  // ... (otros metodos)

  const filteredVehiculos = vehicles.filter(v => {
    const matchSearch = v.placa.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       v.propietario?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchTipo = filterTipo === 'TODOS' || v.tipo === filterTipo;
    return matchSearch && matchTipo;
  });

  return (
    <>
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Vehículos</h1>
            <p className="text-slate-500 text-sm mt-1">Gestión de vehículos registrados</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            disabled={isLoading}
            className="w-full sm:w-auto px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 transition whitespace-nowrap bg-teal-500 hover:bg-teal-600 text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-add-line text-lg"></i>
            Nuevo Vehículo
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
                <input
                  type="text"
                  placeholder="Buscar por placa o propietario..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0">
              {['TODOS', 'AUTO', 'MOTO'].map(tipo => (
                <button
                  key={tipo}
                  onClick={() => setFilterTipo(tipo)}
                  className={`px-4 py-2.5 rounded-xl text-sm font-medium transition whitespace-nowrap ${
                    filterTipo === tipo
                      ? 'bg-teal-500 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {tipo}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Placa</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Tipo</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Vehículo</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Propietario</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Teléfono</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Estado</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                    <tr><td colSpan={7} className="text-center py-8 text-slate-500">Cargando...</td></tr>
                ) : filteredVehiculos.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8 text-slate-500">No hay vehículos registrados</td></tr>
                ) : (
                    filteredVehiculos.map(vehiculo => (
                  <tr key={vehiculo.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4">
                      <span className="font-semibold text-slate-800">{vehiculo.placa}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded-lg text-xs font-medium">
                        <i className={`ri-${vehiculo.tipo === 'AUTO' ? 'car' : vehiculo.tipo === 'MOTO' ? 'motorbike' : 'truck'}-line`}></i>
                        {vehiculo.tipo}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm">
                        <div className="font-medium text-slate-800">{vehiculo.marca} {vehiculo.modelo}</div>
                        <div className="text-slate-500">{vehiculo.color}</div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700">{vehiculo.propietario || '-'}</td>
                    <td className="py-3 px-4 text-sm text-slate-700">{vehiculo.telefono || '-'}</td>
                    <td className="py-3 px-4">
                      {vehiculo.status === 'DENTRO' ? (
                        <div className="flex flex-col gap-1">
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-medium w-fit">
                            <i className="ri-checkbox-circle-fill"></i>
                            DENTRO
                          </span>
                          {vehiculo.horaEntrada && <span className="text-xs text-slate-500">Desde {new Date(vehiculo.horaEntrada).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium">
                          <i className="ri-close-circle-line"></i>
                          FUERA
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button 
                            onClick={() => handleView(vehiculo)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg transition text-slate-600 hover:bg-slate-100 cursor-pointer"
                            title="Ver detalle"
                        >
                          <i className="ri-eye-line"></i>
                        </button>
                        <button 
                            onClick={() => handleEdit(vehiculo)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg transition text-slate-600 hover:bg-slate-100 cursor-pointer"
                            title="Editar"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button 
                            onClick={() => handleDelete(vehiculo.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg transition text-red-600 hover:bg-red-50 cursor-pointer"
                            title="Eliminar"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-800">
                {viewMode ? 'Detalle del Vehículo' : editingId ? 'Editar Vehículo' : 'Nuevo Vehículo'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Placa *</label>
                  <input
                    type="text"
                    value={formData.placa}
                    maxLength={10}
                    disabled={viewMode}
                    onChange={(e) => setFormData({ ...formData, placa: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm disabled:bg-slate-50 disabled:text-slate-500 uppercase"
                    placeholder="Ej: ABC123"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Tipo *</label>
                  <select
                    value={formData.tipo}
                    disabled={viewMode}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm pr-8 disabled:bg-slate-50 disabled:text-slate-500"
                    required
                  >
                    <option value="AUTO">Auto</option>
                    <option value="MOTO">Moto</option>
                    <option value="CAMIONETA">Camioneta</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Marca *</label>
                  <input
                    type="text"
                    value={formData.marca}
                    maxLength={30}
                    disabled={viewMode}
                    onChange={(e) => {
                        const val = e.target.value.toUpperCase();
                        if (/^[A-ZÁÉÍÓÚÑ\s]*$/.test(val)) {
                            setFormData({ ...formData, marca: val });
                        }
                    }}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm disabled:bg-slate-50 disabled:text-slate-500 uppercase"
                    placeholder="Ej: CHEVROLET"
                    required
                  />
                  {!viewMode && <p className="text-xs text-slate-400 mt-1">Solo caracteres alfabéticos.</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Línea *</label>
                  <input
                    type="text"
                    value={formData.modelo}
                    maxLength={30}
                    disabled={viewMode}
                    onChange={(e) => setFormData({ ...formData, modelo: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm disabled:bg-slate-50 disabled:text-slate-500 uppercase"
                    placeholder="Ej: SPARK GT"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Color *</label>
                  <input
                    type="text"
                    value={formData.color}
                    maxLength={20}
                    disabled={viewMode}
                    onChange={(e) => {
                        const val = e.target.value.toUpperCase();
                        if (/^[A-ZÁÉÍÓÚÑ\s]*$/.test(val)) {
                            setFormData({ ...formData, color: val });
                        }
                    }}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm disabled:bg-slate-50 disabled:text-slate-500 uppercase"
                    placeholder="Ej: BLANCO"
                    required
                  />
                  {!viewMode && <p className="text-xs text-slate-400 mt-1">Solo caracteres alfabéticos.</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Propietario *</label>
                  <input
                    type="text"
                    value={formData.propietario}
                    maxLength={30}
                    disabled={viewMode}
                    onChange={(e) => {
                        const val = e.target.value.toUpperCase();
                        if (/^[A-ZÁÉÍÓÚÑ\s]*$/.test(val)) {
                            setFormData({ ...formData, propietario: val });
                        }
                    }}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm disabled:bg-slate-50 disabled:text-slate-500 uppercase"
                    placeholder="Nombre del propietario"
                    required
                  />
                  {!viewMode && <p className="text-xs text-slate-400 mt-1">Solo caracteres alfabéticos.</p>}
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Teléfono *</label>
                   <input
                    type="tel"
                    value={formData.telefono}
                    maxLength={13}
                    disabled={viewMode}
                    onChange={(e) => {
                       const val = e.target.value;
                       if (/^[0-9]*$/.test(val)) {
                           setFormData({ ...formData, telefono: val });
                       }
                    }}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm disabled:bg-slate-50 disabled:text-slate-500"
                    placeholder="Ej: 3001234567"
                    required
                  />
                  {!viewMode && <p className="text-xs text-slate-400 mt-1">Solo números, máximo 13 dígitos.</p>}
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition whitespace-nowrap"
                >
                  {viewMode ? 'Cerrar' : 'Cancelar'}
                </button>
                {!viewMode && (
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 px-4 py-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-xl transition whitespace-nowrap flex justify-center gap-2"
                    >
                        {isSubmitting ? <i className="ri-loader-4-line animate-spin"></i> : 'Guardar Vehículo'}
                    </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
